import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { GridColDef } from '@mui/x-data-grid';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Button from 'components/CustomButtons/Button.js';
import DataGrid from 'components/DataGrid/DataGrid';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import config from '../../config/config.json';
import LoginPrompt from '../LoginPrompt/LoginPrompt';

const useStyles = makeStyles<Theme>((theme) => ({
  card: {
    marginBottom: theme.spacing(2),
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
  },
  cardHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textAlign: 'center',
  },
  cardBody: {
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
    width: '100%',
  },
  dataGridContainer: {
    height: 400,
    width: '100%',
    marginTop: theme.spacing(2),
    '& .MuiDataGrid-root': {
      width: '100%',
    },
  },
}));

interface SavedSearchesProps {
  onSearchSelect?: (searchTerm: string) => void;
}

export default function SavedSearches({ onSearchSelect }: SavedSearchesProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedSearchIds, setSelectedSearchIds] = useState<number[]>([]);

  const sessionToken = localStorage.getItem('session_token');
  const isAuthenticated = !!sessionToken;

  const fetchSavedSearches = async () => {
    if (!sessionToken) {
      return [];
    }

    const response = await axios.get(`${config.backend_base_url}ml/search/mine`, {
      headers: { 'session-token': sessionToken },
    });
    return response.data || [];
  };

  const {
    data: savedSearches = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['savedSearches'],
    queryFn: fetchSavedSearches,
    enabled: isAuthenticated,
  });

  if (error) {
    console.error('Query error:', error);
    toast.error(t('search.errors.loadFailed'));
  }

  const deleteMutation = useMutation({
    mutationFn: async (searchIds: number[]) => {
      if (!sessionToken) throw new Error('No session token');

      await axios.post(
        `${config.backend_base_url}ml/search/delete`,
        { search_ids: searchIds },
        { headers: { 'session-token': sessionToken } }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['savedSearches'] });
      toast.success(t('search.filters.deleteSuccess'));
      setSelectedSearchIds([]);
    },
    onError: () => {
      toast.error(t('search.errors.deleteFailed'));
    },
  });

  const columns: GridColDef[] = [
    { field: 'search_name', headerName: t('search.filters.name'), flex: 1 },
    { field: 'search_term', headerName: t('search.filters.term'), flex: 2 },
    { field: 'search_count', headerName: t('search.filters.count'), width: 100 },
  ];

  const handleDeleteSelected = () => {
    if (selectedSearchIds.length === 0) return;
    deleteMutation.mutate(selectedSearchIds);
  };

  const handleRowClick = (params: any) => {
    if (onSearchSelect) {
      onSearchSelect(params.row.search_term);
    }
  };

  return (
    <GridContainer justify="center">
      <GridItem xs={12} sm={12} md={12}>
        <Card className={classes.card}>
          <CardBody className={classes.cardBody}>
            {!isAuthenticated ? (
              <LoginPrompt message={t('search.filters.loginRequired')} />
            ) : (
              <>
                <div className={classes.dataGridContainer}>
                  <DataGrid
                    columns={columns}
                    rows={savedSearches}
                    loading={isLoading}
                    checkboxSelection
                    rowSelectionModel={selectedSearchIds}
                    onRowSelectionModelChange={(newSelection) => {
                      setSelectedSearchIds(newSelection as number[]);
                    }}
                    onRowClick={handleRowClick}
                    sx={{ width: '100%' }}
                  />
                </div>
                {selectedSearchIds.length > 0 && (
                  <Button
                    color="danger"
                    fullWidth
                    disabled={selectedSearchIds.length === 0}
                    onClick={handleDeleteSelected}
                  >
                    {t('search.filters.deleteSelectedSearches')}
                  </Button>
                )}
              </>
            )}
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
